import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Scale, Handshake } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { LEGAL, LEGAL_UPDATED } from "@/lib/legal";
import { VinconexLogo } from "@/components/VinconexMark";

const ICONS = { privacy: FileText, terms: Scale, security: ShieldCheck, dpa: Handshake };
const NAV = [["privacy", "Privacy"], ["terms", "Terms"], ["security", "Security"], ["dpa", "DPA"]];

export default function Legal({ kind }) {
  const doc = LEGAL[kind];
  const Icon = ICONS[kind];
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid={`legal-${kind}`}>
      <header className="border-b border-[#1C2233]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <VinconexLogo variant="wordmark" className="h-5 w-auto" />
          <nav className="flex items-center gap-4 text-xs sm:text-sm">
            {NAV.map(([k, label]) => (
              <Link key={k} to={`/${k}`} data-testid={`legal-nav-${k}`} className={k === kind ? "text-white font-semibold" : "text-slate-400 hover:text-white"}>{label}</Link>
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#0A8CF2] font-mono-tech mb-4"><Icon className="h-4 w-4" /> {BRAND.owner} · Vinconex</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="legal-title">{doc.title}</h1>
        <p className="text-xs text-slate-500 mt-3 font-mono-tech" data-testid="legal-updated">Last updated {LEGAL_UPDATED}</p>
        <p className="text-base text-slate-300 mt-8 leading-relaxed max-w-3xl" data-testid="legal-intro">{doc.intro}</p>
        <ol className="mt-12 space-y-10">
          {doc.sections.map(([h, body], i) => (
            <li key={h} className="grid sm:grid-cols-[56px_1fr] gap-3 sm:gap-6" data-testid={`legal-section-${i + 1}`}>
              <div className="font-heading text-2xl font-black text-[#0A8CF2]/70">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h2 className="text-base md:text-lg font-bold">{h}</h2>
                <p className="text-sm sm:text-base text-slate-400 mt-2 leading-relaxed">{body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-16 rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5 text-sm text-slate-400" data-testid="legal-contact">
          Questions about this document or a data request? Email <a href="mailto:contact@vinconex.com" className="text-[#7FC3FF] hover:text-white">contact@vinconex.com</a>. {BRAND.owner} operates Vinconex.
        </div>
      </main>
      <footer className="border-t border-[#1C2233] py-8">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/contact" className="hover:text-white">Contact us</Link>
            {NAV.filter(([k]) => k !== kind).map(([k, label]) => <Link key={k} to={`/${k}`} className="hover:text-white">{label}</Link>)}
          </div>
          <span data-testid="owner-line">© {new Date().getFullYear()} {BRAND.owner} · Vinconex is a {BRAND.owner} platform</span>
        </div>
      </footer>
    </div>
  );
}
